<template>
    <div>
        <!-- 暂停领取弹框 -->
        <van-dialog v-model:show="showPausePopup" title="暂停领取" :show-cancel-button="true" :before-close="handlePauseClose">
            <div class="pause-popup">
                <div class="date-select">
                    <div class="label">暂停日期：</div>
                    <div class="date-range" @click="showDatePicker = true">
                        <span>{{ pauseStartDate || '开始日期' }} - {{ pauseEndDate || '结束日期' }}</span>
                        <van-icon name="calendar-o" />
                    </div>
                </div>
                <div class="pause-notice">
                    <div class="notice-title">暂停说明</div>
                    <div class="notice-list">
                        <template v-for="line in pauseNotice.split('\n')" :key="line">
                            <div class="notice-item">{{ line }}</div>
                        </template>
                    </div>
                </div>
            </div>
        </van-dialog>

        <!-- 日期选择器 -->
        <van-calendar v-model:show="showDatePicker" type="range" :min-date="minDate" :max-date="maxDate" :allow-same-day="true"
            @confirm="onDateConfirm" color="#8B0000" />
    </div>
</template>

<script setup>
import { ref, watch } from 'vue'
import { post } from '@/utils/request'
import { Toast, Dialog } from 'vant'

// 定义props
const props = defineProps({
    orderId: {
        type: [String, Number],
        required: true,
        default: ''
    },
    placeId: {
        type: [String, Number],
        required: true,
        default: ''
    }
})

// 定义emits
const emit = defineEmits(['success'])

// 响应式数据
const showPausePopup = ref(false)
const showDatePicker = ref(false)
const pauseStartDate = ref('')
const pauseEndDate = ref('')
const pauseNotice = ref('')
const minDate = ref(new Date())
const maxDate = ref((() => {
    const date = new Date()
    date.setFullYear(date.getFullYear() + 1)
    return date
})())

// 监听orderId变化
watch(() => props.orderId, (newVal) => {
    // 当orderId变化时，重置组件状态
    if (!newVal) {
        resetState()
    }
}, { immediate: true })

// 重置组件状态
const resetState = () => {
    showPausePopup.value = false
    showDatePicker.value = false
    pauseStartDate.value = ''
    pauseEndDate.value = ''
    pauseNotice.value = ''
}

// 显示暂停弹窗
const showModal = async () => {
    if (!props.placeId) {
        Toast('网点信息不存在')
        return
    }
    if (!props.orderId) {
        Toast('订单信息不存在')
        return
    }

    try {
        const res = await post('/api/select/pause/desc', {
            place_id: props.placeId
        })
        if (res.result.state === 'ok') {
            pauseNotice.value = res.result.desc
            showPausePopup.value = true
            pauseStartDate.value = ''
            pauseEndDate.value = ''
        } else {
            Dialog.alert({
                title: '提示',
                message: res.result.message
            })
        }
    } catch (error) {
        console.error('查询网点暂停取奶说明失败:', error)
        Toast.fail('查询网点暂停取奶说明失败')
    }
}

// 日期选择确认
const onDateConfirm = ([start, end]) => {
    pauseStartDate.value = formatDate(start)
    pauseEndDate.value = formatDate(end)
    showDatePicker.value = false
}

// 格式化日期
const formatDate = (date) => {
    const d = new Date(date)
    return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`
}

// 处理暂停确认
const handlePauseClose = (action) => {
    return new Promise((resolve) => {
        if (action === 'confirm') {
            if (!pauseStartDate.value || !pauseEndDate.value) {
                Toast('请选择暂停日期')
                resolve(false)
                return
            }

            // 执行暂停操作
            executePause().then(() => {
                resolve(true)
            }).catch(() => {
                resolve(false)
            })
        } else {
            resolve(true)
        }
    })
}

// 执行暂停操作
const executePause = async () => {
    try {
        const res = await post('/api/pause/collection', {
            order_id: props.orderId,
            pause_start_date: pauseStartDate.value.replace(/\//g, '-'),
            pause_end_date: pauseEndDate.value.replace(/\//g, '-')
        })

        if (res.result.state === 'ok') {
            Toast.success('暂停成功')
            showPausePopup.value = false
            emit('success')
        } else {
            Dialog.alert({
                title: '提示',
                message: res.result.message
            })
        }
    } catch (error) {
        console.error('暂停订单失败:', error)
        Toast.fail('暂停失败，请重试')
    }
}

// 暴露方法给父组件
defineExpose({
    show: showModal
})
</script>

<style scoped>
.pause-popup {
    padding: 10px;
}

.date-select {
    display: flex;
    align-items: center;
    padding: 10px;
    background: #F5F5F5;
    border-radius: 5px;
}

.date-select .label {
    font-size: 14px;
    color: #333;
    margin-right: 10px;
}

.date-select .date-range {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 14px;
    color: #666;
}

.date-select .date-range .van-icon {
    color: #999;
    font-size: 17px;
}

.pause-notice {
    margin-top: 16px;
    padding: 10px;
    border-radius: 5px;
    background: #F5F5F5;
}

.pause-notice .notice-title {
    font-size: 14px;
    font-weight: 500;
    color: #333;
    margin-bottom: 10px;
}

.pause-notice .notice-list .notice-item {
    font-size: 12px;
    color: #666;
    line-height: 1.8;
}
</style>
